import React, { useState, useContext, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Info,
  CheckCircle2,
  XCircle,
  Undo,
  Loader,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Layout } from './Layout';
import { AppContext } from '../src/App';
import { useAuth } from './AuthContext';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Textarea } from './ui/textarea';
import { apiClient } from '@/Utils/apiClient';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';


interface Widget {
  widget_type: 'field' | 'group' | 'table' | 'note';
  label?: string;
  value?: string;
  field_name?: string;
  group_name?: string;
  fields?: Widget[];
  columns?: { label: string; field_name: string }[];
  rows?: any[];
  note_text?: string;
}

interface Section {
  section_number: number;
  section_title: string;
  widgets: Widget[];
  adjudication?: string;
  not_applicable?: boolean;
  NoteToClinician?: string;
}

interface RNChartRecord {
  id: string; // Changed to string to support UUIDs
  chartId: string;
  patientName: string;
  clinicianName: string;
  visitDate: string;
  promptName: string;
  promptVersion: string;
  modelName: string;
  modelProvider: string;
  aiDecision: 'approve' | 'review';
  aiReason: string;
  visitStart?: string;
  visitEnd?: string;
  visitDuration?: string;
  shiftNarrative?: string;
  todaysNotes?: string;
  signaturePresent?: string;
  clinicianSignature?: string;
  clientSignedOnDate?: string;
  clientSignedOnTime?: string;
  clientAlert?: string;
  chartData: {
    sections: Section[];
  };
}

// --- 2. API TYPES (Matching the JSON you provided) ---


// // --- 3. TRANSFORMER FUNCTION (API -> UI) ---
const transformAuditResponse = (apiData: any) : RNChartRecord => {
  
//   // Recursive function to handle nested groups
  const mapWidget = (apiW: any): Widget => {
    // 1. Handle Groups
    if (apiW.type === 'group' && apiW.items) {
      return {
        widget_type: 'group',
        group_name: apiW.label,
        fields: apiW.items.map(mapWidget)
      };
    }

    // 2. Handle Fields (Convert values based on datatype)
    let displayValue = String(apiW.value || '-');
    
    if (apiW.datatype === 'checkbox') {
      if (apiW.value === true) displayValue = 'Yes';
      else if (apiW.value === false) displayValue = 'No';
      else displayValue = '-';
    }

    return {
      widget_type: 'field',
      label: apiW.label,
      value: displayValue,
      field_name: apiW.key,
    };
  };

//   // Merge Sections from chart_data (widgets) and ai_metadata (adjudication)
  const chartSections: any[] = apiData.chart_data?.sections || [];
  const aiSections: any[] = apiData.ai_metadata?.content?.sections || [];
  
  const rawSections = chartSections.length > 0 ? chartSections : aiSections;

  const mappedSections: Section[] = rawSections.map((sec : any, idx : number) => {
    // Find matching AI section
    const aiSec = aiSections.find(a => 
        (a.id && sec.id && a.id.toLowerCase() === sec.id.toLowerCase()) || 
        (a.title && sec.section_title && a.title.toLowerCase() === sec.section_title.toLowerCase())
    ) || aiSections[idx] || {};

    return {
      section_number: idx + 1,
      section_title: sec.section_title || aiSec.title || sec.title || `Section ${idx + 1}`,
      widgets: sec.widgets ? sec.widgets.map(mapWidget) : [],
      adjudication: aiSec.adjudication,
      not_applicable: aiSec.not_applicable,
      NoteToClinician: aiSec.NoteToClinician || aiSec.noteToClinician || ''
    };
  });


  // Map Adjudication
  const decisionRaw = apiData.ai_metadata?.content?.OverallAuditDecision || '';
  const aiDecision = decisionRaw.toLowerCase().includes('approv') ? 'approve' : 'review';

  return {
    id: apiData.id,
    chartId: apiData.chart_id,
    patientName: apiData.client_name,
    clinicianName: apiData.clinician_name,
    visitDate: apiData.visit_date,
    promptName: apiData.prompt_name,
    promptVersion: apiData.prompt_version,
    modelName: apiData.ai_metadata?.model || 'Unknown',
    modelProvider: apiData.ai_metadata?.provider || 'Unknown',
    aiDecision: aiDecision,
    aiReason: apiData.ai_metadata?.content?.overallAuditFeedback || 'No reasoning provided.',
    visitStart: apiData.chart_data?.visit?.checkInTime || apiData.ai_metadata?.content?.visit?.checkInTime,
    visitEnd: apiData.chart_data?.visit?.checkOutTime || apiData.ai_metadata?.content?.visit?.checkOutTime,
    visitDuration: apiData.chart_data?.visit?.hours || apiData.ai_metadata?.content?.visit?.hours,
    shiftNarrative: apiData.ai_metadata?.content?.visit?.sectionShiftNarrative,
    todaysNotes: apiData.ai_metadata?.content?.visit?.todaysNotes,
    signaturePresent: apiData.ai_metadata?.content?.visit?.signaturePresent,
    clinicianSignature: apiData.ai_metadata?.content?.visit?.clinicianSignature,
    clientSignedOnDate: apiData.ai_metadata?.content?.visit?.clientSignedOnDate,
    clientSignedOnTime: apiData.ai_metadata?.content?.visit?.clientSignedOnTime,
    clientAlert: apiData.ai_metadata?.content?.visit?.clientAlert,
    chartData: {
      sections: mappedSections
    }
  };
};

/**
 * RN Chart Detail View - Wraps RN Chart Option 4 with real data and navigation
 */

export function RNChartDetailView() {
  const { user, token } = useAuth();
  const { selectedAuditData, sideBarItems, previousViewScreen, setCurrentView, currentIndexOfListedChart, setSelectedAuditData, setCurrentIndexOfListedChart,
    currentIndexOfClinicianChart,
    setCurrentIndexOfClinicianChart,
    orgConfig } = React.useContext(AppContext);
  
  // Removed harvestRuns, using a single chart state now
  const [chart, setChart] = useState<RNChartRecord | null>(null); 
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewedSections, setReviewedSections] = useState<Set<string>>(new Set());
  const [isPromptDetailsExpanded, setIsPromptDetailsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const chartReviewMenu = sideBarItems.find((item: any) => item.name === "Chart Review");
  const [harvestRuns, setHarvestRuns] = useState<any>([])
  const [chartData, setChartData] = useState<any>([])
  const [showAuditActionDialog, setShowAuditActionDialog] = useState(false);
  const [pendingAuditAction, setPendingAuditAction] = useState<'approve' | 'reject' | null>(null);
  const [auditActionComment, setAuditActionComment] = useState('');
  const [selectedUndoCharts, setSelectedUndoCharts] = useState<any>(null);
  const [showNextOrPrev, setShowNextOrPrev] = useState({ hasPrev: false, hasNext: false });
  const [totalCount, setTotalCount] = useState(0);
  const hasPermission = (permission: string) => {
    return chartReviewMenu?.userPermissions?.includes(permission);
  }
    const isFromHarvestRun = previousViewScreen === 'harvest-run-detail';
  const activeChartIndexState = isFromHarvestRun
    ? currentIndexOfListedChart
    : currentIndexOfClinicianChart;
    
  const setActiveChartIndexState = isFromHarvestRun
    ? setCurrentIndexOfListedChart
    : setCurrentIndexOfClinicianChart;
    const parentViewName = isFromHarvestRun 
    ? 'harvest-run-detail' 
    : 'client-clinician-view';
  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };
  // Fetching real-time data
  const fetchData = async () => {
    try {
      if (!selectedAuditData?.id) {
        console.warn("fetchData called without a selectedAuditData.id. Aborting.");
        return;
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/${selectedAuditData?.id}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      setHarvestRuns(data?.data?.audit)
      setChartData(data?.data?.audit?.ai_metadata?.content?.goalUnits)
       if (data.success && data.data?.audit) {
          const transformed = transformAuditResponse(data.data.audit);
          setChart(transformed);
          // Optional: Auto-expand all sections on load
          // setExpandedSections(new Set(transformed.chartData.sections.map(s => s.section_number)));
        } else {
          throw new Error('Invalid data structure received');
        }
    } catch (error: any) {
      console.error('Fetch error:', error.message);
      setChart(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedAuditData]); // Depend on selectedAuditData and token

    const handleAuditAction = (action: 'approve' | 'reject') => {
    setPendingAuditAction(action);
    setAuditActionComment('');
    setShowAuditActionDialog(true);
  };

   const confirmAuditAction = async () => {
    if (!pendingAuditAction) return;

    const newDataToSend = {
      outcome: pendingAuditAction === 'approve' ? 'Approved' : 'Rejected',
      supervisorComment: auditActionComment.trim() || ""

    }

    console.log(newDataToSend, "newDataToSend")

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/${selectedAuditData?.id}`, {

        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newDataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      // const newData = await generateMockHarvestData(data)
      setHarvestRuns(data?.data?.audit)
      setChartData(data?.data?.audit?.ai_metadata?.content?.goalUnits)
      fetchData()
      // setCurrentView('harvest-run-detail')
      handleNext();
      return data;
    } catch (error: any) {
      console.error('Fetch error:', error.message);
      return null;
    }
  };

    const handleUndoAuditAction = async () => {
  
    try{
        const response= await apiClient(`audits/${selectedUndoCharts}`,{
          method:'PUT',
          token,
         body:{
            outcome:"Pending",
            supervisorComment:""
          }}
         
    
        )
          
          const data = await response.json();
          if(data?.success){
            toast.success('Undo Decision successfully');
          }
    
        }
        catch(error: any){
          console.error('Error fetching categories:', error.message);
        }
        finally{
          fetchData();
          setSelectedUndoCharts(null);
        }
  }

    const fetchCurrentChart=async()=>{
       if (activeChartIndexState.filterItems.length > 0) {
        const total = activeChartIndexState.filterItems.length;
        setTotalCount(total);
      setShowNextOrPrev({
        hasPrev: activeChartIndexState.index > 0,
        hasNext:
          activeChartIndexState.index <
          activeChartIndexState.filterItems.length - 1,
      });
  
      const data = activeChartIndexState.filterItems[activeChartIndexState.index];
      setSelectedAuditData({ ...selectedAuditData, id: data?.id });
    }
    else{
      if (isFromHarvestRun) {
          try {
            const response=await apiClient(`harvest/runs/${selectedAuditData?.run_id}`,{
        method:'GET',
        token
      })
      const result=await response.json();
      setShowNextOrPrev({
        hasPrev:currentIndexOfListedChart.index>0,
        hasNext:currentIndexOfListedChart.index<result?.data?.harvestRun?.audits?.length-1
      })
      setTotalCount(result?.data?.harvestRun?.audits?.length);
      let data=result?.data?.harvestRun?.audits[currentIndexOfListedChart.index];
      setSelectedAuditData({...selectedAuditData,id:data?.id})
          } catch (e) {
            console.error("Failed to fetch harvest run list:", e);
          }
  
        } else {
     
          try {
            const response = await apiClient(`audits/all?page=${activeChartIndexState.currentPage}&limit=15`, { // No filters, no pagination
              method: 'GET',
              token
            });
            const result = await response.json();
            const audits = result?.data?.audits || []; // The response has { data: { audits: [...] } }
             setTotalCount(audits.length);
            setShowNextOrPrev({
              hasPrev: activeChartIndexState.index > 0,
              hasNext: activeChartIndexState.index < audits.length - 1
            });
  
              let data = audits[activeChartIndexState.index];
              setSelectedAuditData({...selectedAuditData,id:data?.id})
          } catch (e) {
            console.error("Failed to fetch all audits list:", e);
          }
        }
      }
    
     
    }
    useEffect(()=>{
      fetchCurrentChart()
    },[activeChartIndexState.index])

  const handlePrevious = () => {
  if (!showNextOrPrev.hasPrev) {
    setCurrentView(parentViewName);
    return;
  }

  setActiveChartIndexState((prev: { index: number }) => ({
    ...prev,
    index: prev.index - 1,
  }));
};

const handleNext = () => {
  if (!showNextOrPrev.hasNext) {
    setCurrentView(parentViewName);
    return;
  }

  setActiveChartIndexState((prev: { index: number })=> ({
    ...prev,
    index: prev.index + 1,
  }));
};

  const handleBackToBatch = () => {
    setCurrentView(parentViewName);
  };

  const handleApprove = () => {
    // TODO: Implement approval logic
  };

  const handleRequestCorrections = () => {
    // TODO: Implement request corrections logic
  };

  // Show error if no chart selected
  if (!harvestRuns || !chart) {
    return (
      <Layout>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-base font-bold text-gray-900 mb-2">Loading Charts....</h1>
          {/* <p className="text-gray-600 mb-4">The selected audit ID did not return valid chart data.</p> */}
          {/* <Button onClick={handleBackToBatch}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Batch
          </Button> */}
          {/* <Loader2 className="w-16 h-16 mx-auto" /> */}
        </div>
      </div>
      </Layout>
    );
  }

  // Filter sections based on search
  const filteredSections = searchQuery
    ? chart.chartData.sections.filter(section =>
        section.section_title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : chart.chartData.sections;

  // Toggle section as reviewed
  const toggleSectionReview = (sectionNumber: number) => {
    const key = `section-${sectionNumber}`;
    setReviewedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Toggle section expansion
  const toggleSectionExpansion = (sectionNumber: number) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionNumber)) {
        newSet.delete(sectionNumber);
      } else {
        newSet.add(sectionNumber);
      }
      return newSet;
    });
  };

  // Render different widget types
  const renderWidget = (widget: Widget, sectionNumber: number) => {
    switch (widget.widget_type) {
      case 'field':
        return (
          <div key={widget.field_name} className="bg-white rounded-lg border border-gray-200 px-[8px] py-[4px]">
            <div className="text-gray-500 mb-0.5" style={{ fontSize: '10px' }}>{widget.label}</div>
            <div className="text-gray-900" style={{ fontSize: '12px' }}>{widget.value || 'N/A'}</div>
          </div>
        );

      case 'group':
        return (
          <div key={widget.group_name} className="space-y-2">
            <h4 className="text-gray-900" style={{ fontSize: '11px' }}>{widget.group_name}</h4>
            <div className="grid grid-cols-3 gap-2">
              {widget.fields?.map(field => (
                <div key={field.field_name} className="bg-white rounded-lg border border-gray-200 p-2">
                  <div className="text-gray-500 mb-0.5" style={{ fontSize: '10px' }}>{field.label}</div>
                  <div className="text-gray-900" style={{ fontSize: '12px' }}>{field.value || 'N/A'}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'table':
        // NOTE: The new API data structure does not include tables, but we retain 
        // the rendering logic from the original mock data just in case it's added back.
        return (
          <div key={widget.label} className="space-y-1">
            {widget.label && <h4 className="text-gray-900" style={{ fontSize: '11px' }}>{widget.label}</h4>}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {widget.columns?.map(col => (
                      <TableHead key={col.field_name} style={{ fontSize: '10px' }}>{col.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {widget.rows?.map((row, idx) => (
                    <TableRow key={idx}>
                      {widget.columns?.map(col => (
                        <TableCell key={col.field_name} style={{ fontSize: '10px' }}>{row[col.field_name] || '-'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );

      case 'note':
        // NOTE: The new API data structure does not include 'note' type widgets,
        // but this logic is retained from the original component.
        return (
          <div key={`note-${sectionNumber}`} className="bg-blue-50 border border-blue-200 rounded-lg p-2">
            <div className="flex items-start gap-2">
              <Info className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700" style={{ fontSize: '10px' }}>{widget.note_text}</div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render section content
  const renderSection = (section: Section) => {
    const isReviewed = reviewedSections.has(`section-${section.section_number}`);
    const isExpanded = expandedSections.has(section.section_number);

    // Check if section has only standalone fields (should display as 3-column grid)
    const hasOnlyFields = section.widgets.every(w => w.widget_type === 'field');

    // Evaluate AI Adjudication for sections dynamically
    // Render whatever adjudication the AI returned — do not override based on not_applicable.
    const isSectionApproved = section.adjudication === 'SectionIsApproved';

    return (
      <div key={section.section_number} className="border-b border-gray-200 last:border-b-0">
        {/* Section Row - 3 Column Grid */}
        <div className="grid grid-cols-[70%_15%_15%]">
          {/* Column 1: Section Header + Content */}
          <div className="border-r border-gray-200">
            {/* Section Header */}
            <div
              className="bg-gray-50 py-[6px] border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-100 px-[12px] py-[4px]"
              onClick={() => toggleSectionExpansion(section.section_number)}
            >
              <div className="flex items-center gap-2">
                {/* Chevron Icon for Expand/Collapse */}
                <button
                  className="flex items-center justify-center w-5 h-5 rounded hover:bg-gray-200 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSectionExpansion(section.section_number);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600" style={{ fontSize: '10px' }}>
                  {section.section_number}
                </div>
                <h3 className="text-gray-900" style={{ fontSize: '11px' }}>{section.section_title}</h3>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-gray-500" style={{ fontSize: '9px' }}>{section.widgets.length} items</p>
                {isReviewed && (
                  <Badge variant="default" className="bg-green-100 text-green-700" style={{ fontSize: '9px', padding: '2px 6px' }}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Reviewed
                  </Badge>
                )}
              </div>
            </div>

            {/* Section Content - Collapsible */}
            {isExpanded && (
              <div className="px-[12px] py-[8px]">
                {hasOnlyFields ? (
                  // Standalone fields in 3-column grid
                  <div className="grid grid-cols-3 gap-2">
                    {section.widgets.map(widget => renderWidget(widget, section.section_number))}
                  </div>
                ) : (
                  // Other widget types in natural flow
                  <div className="space-y-3">
                    {section.widgets.map(widget => renderWidget(widget, section.section_number))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Column 2: Adjudication - Simulating data as not present in API section object */}
          <div className={`border-r border-gray-200 px-2 py-2 flex items-center justify-center ${isSectionApproved ? 'bg-green-50/30' : 'bg-red-50/30'}`}>
            <div className="text-center">
              <Badge 
                className={`text-center ${isSectionApproved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} 
                style={{ fontSize: '9px', padding: '2px 6px' }}
              >
                {isSectionApproved ? 'Approved' : 'Review'}
              </Badge>
            </div>
          </div>

          {/* Column 3: Note to Clinician */}
          <div className="bg-gray-50/30 px-2 py-2 flex items-center justify-center text-center">
            {section.NoteToClinician ? (
              <div className="text-gray-700 font-medium" style={{ fontSize: '10px' }}>
                {section.NoteToClinician}
              </div>
            ) : (
              <div className="text-gray-400" style={{ fontSize: '9px' }}>
                ---
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Determine header badge style and text
  const isApproved = harvestRuns?.ai_metadata?.content.OverallAuditDecision === 'Approved' || harvestRuns?.conformance_status === 'COMPLIANT';
  const decisionBadgeText = isApproved ? 'Approved' : 'Needs Review';

  return (
    <Layout>
      <div className="h-screen flex flex-col bg-white">
        {/* Breadcrumb Navigation */}
        <div className="bg-white border-b border-gray-200 px-6 py-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="hover:text-gray-900 cursor-pointer" onClick={() => { /* Navigate to dashboard */ }}>
              Chart Review Dashboard
            </span>
            <span>/</span>
            <span className="hover:text-gray-900 cursor-pointer" onClick={handleBackToBatch}>
              Batch Detail View
            </span>
            <span>/</span>
            <span className="text-gray-900 font-medium">SN Chart Review</span>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white border-b">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={handleBackToBatch}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Batch
                </Button>
                <div>
                  <h1 className="text-gray-900 m-0 text-lg font-semibold">
                    SN Chart Review - {harvestRuns.client_name}
                  </h1>
                  <p className="text-gray-600 text-sm">
                    Chart ID: {harvestRuns.chartId} | Visit: {harvestRuns?.chart_data?.visit?.visitDate}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Previous/Next Navigation (Disabled for single chart view) */}
                <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    onClick={handlePrevious}
                    disabled={!showNextOrPrev.hasPrev} // Disable as only one chart is loaded
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Separator orientation="vertical" className="h-6" />
                  <span className="text-sm text-gray-500 px-2">
                    Chart {activeChartIndexState.index + 1} of {totalCount || 1}
                  </span>
                  <Separator orientation="vertical" className="h-6" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    onClick={handleNext}
                    disabled={!showNextOrPrev.hasNext} // Disable as only one chart is loaded
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                 {hasPermission("audits:review") && harvestRuns?.outcome !== "Rejected" && harvestRuns?.outcome !== "Approved" && (
                    <>
                      <Button
                        onClick={() => handleAuditAction('approve')}
                        className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Agree with AI
                      </Button>
                      <Button
                        onClick={() => handleAuditAction('reject')}
                        variant="destructive"
                        className="flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Disagree with AI
                      </Button>
                    </>
                  )}
                  {hasPermission("audits:review") && (harvestRuns?.outcome === "Approved" || harvestRuns?.outcome === "Rejected") ? (
                    <Button
                        onClick={() => setSelectedUndoCharts(harvestRuns?.id)}
                        className="bg-gray-500 hover:bg-gray-600 text-white flex items-center gap-2"
                      >
                        <Undo className="w-4 h-4" />
                        Undo Decision
                      </Button>
                  ):null

                  }
              </div>
            </div>
          </div>
        </div>

        {/* AI Decision Alerts */}
        {harvestRuns?.ai_metadata?.content?.OverallAuditDecision && harvestRuns?.ai_metadata?.content?.overallAuditFeedback && (
          <div className="bg-white border-b px-6 py-3">
            <div className={`grid gap-4 ${harvestRuns?.outcome === "Rejected" || harvestRuns?.outcome === "Approved" ? "grid-cols-2" : "grid-cols-1"}`}>
              {/* Overall Chart Review Decision Alert - AI Decision */}
              <div className={` bg-blue-50 border border-blue-200 rounded-lg p-3`}>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-medium text-blue-900`} style={{ fontSize: '12px' }}>Overall Chart Review Decision</span>
                    </div>
                    <p className={`text-blue-800 mb-1`} style={{ fontSize: '11px' }}>
                      <strong>AI Reasoning:</strong> {harvestRuns?.ai_metadata?.content?.overallAuditFeedback}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <span>Decision: {decisionBadgeText}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disagree with AI Alert - Auditor Decision (Mocked) */}
              {(harvestRuns?.outcome === "Rejected" || harvestRuns?.outcome === "Approved") && (
              <div className={`${harvestRuns?.outcome === 'Approved' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'} rounded-lg p-3`}>
                <div className="flex items-start gap-2">
                  {harvestRuns?.outcome === 'Approved' ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : (
                    <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                   )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium ${harvestRuns?.outcome === 'Approved' ? 'text-green-900' : 'text-red-900'}`} style={{ fontSize: '12px' }}>
                        {harvestRuns?.outcome === 'Approved' ? 'Agree with AI' : 'Disagree with AI'}
                      </span>
                      {harvestRuns?.outcome === 'Approved' ? (
                         <Badge variant="outline" className="bg-green-600 text-white">
                        Agreed
                      </Badge>
                      ) : (
                         <Badge variant="destructive" className="bg-red-600 text-white">
                        Disagreed
                      </Badge>
                      )}
                     
                    </div>
                    <div className="space-y-1" style={{ fontSize: '11px' }}>
                      <div className={`${harvestRuns?.outcome === 'Approved' ? 'text-green-800' : 'text-red-800'}`}>
                        <strong>Auditor Name:</strong> {harvestRuns?.auditor_name}
                      </div>
                      <div className={`${harvestRuns?.outcome === 'Approved' ? 'text-green-800' : 'text-red-800'}`}>
                        <strong>Date:</strong> {new Date(harvestRuns?.approved_reject_date)?.toLocaleDateString('en-US')}  Time : {new Date(harvestRuns?.approved_reject_date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', })}
                      </div>
                      <div className={`${harvestRuns?.outcome === 'Approved' ? 'text-green-800' : 'text-red-800'}`}>
                        <strong>Comments to Aukera Health:</strong>{harvestRuns?.supervisor_comment?.trim() || "None"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        )}

        {/* Prompt & Model Details (Collapsible) */}
        <div className="bg-white border-b px-6 py-3">
          <div className="border border-gray-200 rounded-lg">
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 px-[16px] py-[4px]"
              onClick={() => setIsPromptDetailsExpanded(!isPromptDetailsExpanded)}
            >
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-900" style={{ fontSize: '12px' }}>Prompt & Model Details</span>
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                {isPromptDetailsExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Expanded Details */}
            {isPromptDetailsExpanded && (
              <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                <div className="grid grid-cols-4 gap-4" style={{ fontSize: '11px' }}>
                  <div>
                    <div className="text-gray-500 mb-1">Prompt Name</div>
                    <div className="text-gray-900 font-medium font-mono">{harvestRuns?.prompt_name}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Prompt Version</div>
                    <div className="text-gray-900 font-medium font-mono">{harvestRuns?.prompt_version}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Model Name</div>
                    <div className="text-gray-900 font-medium font-mono">{harvestRuns?.ai_metadata?.model}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Model Provider</div>
                    <div className="text-gray-900 font-medium font-mono">{harvestRuns?.ai_metadata?.provider}</div>
                  </div>
                </div>

                {/* Second row */}
                <div className="grid grid-cols-4 gap-4 mt-3" style={{ fontSize: '11px' }}>
                  <div>
                    <div className="text-gray-500 mb-1">Temperature</div>
                    <div className="text-gray-900 font-medium font-mono">N/A</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Max Tokens (Mock)</div>
                    <div className="text-gray-900 font-medium font-mono">N/A</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Processed At (Scrape End Time)</div>
                    <div className="text-gray-900 font-medium">{harvestRuns?.ai_metadata?.content?.visit?.visitDate}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Processing Time</div>
                    <div className="text-gray-900 font-medium">{formatTime(harvestRuns?.ai_metadata?.content?.proccessingTime)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chart Details Section */}
        <div className="bg-gray-50 border-b px-6 py-3">
          <div className="grid grid-cols-5 gap-6" style={{ fontSize: '11px' }}>
            {/* Row 1 */}
            <div>
              <div className="text-gray-500 mb-1">Client Id</div>
              <div className="text-gray-900 font-medium">{harvestRuns?.client_id}</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Client</div>
              <div className="text-gray-900 font-medium">{harvestRuns?.client_name}</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Clinician</div>
              <div className="text-gray-900 font-medium">{harvestRuns?.clinician_name}</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Visit Date</div>
              <div className="text-gray-900 font-medium">{harvestRuns?.chart_data?.visit?.visitDate ?? ""}</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Check In</div>
              <div className="text-gray-900 font-medium">{harvestRuns?.chart_data?.visit?.checkInTime}</div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-5 gap-6 mt-3" style={{ fontSize: '11px' }}>
            <div>
              <div className="text-gray-500 mb-1">Check Out</div>
              <div className="text-gray-900 font-medium">{ harvestRuns?.chart_data?.visit?.checkOutTime } </div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Hours Spent</div>
              <div className="text-gray-900 font-medium">{ harvestRuns?.chart_data?.visit?.hours }</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Total Goals</div>
              <div className="text-gray-900 font-medium">{harvestRuns?.ai_metadata?.content?.goalUnits?.length}</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">{`${orgConfig?.name} Status`}</div>
              {harvestRuns?.kt_update && (
              <Badge variant="default" className="bg-green-100 text-green-700">
                {harvestRuns?.kt_update}
              </Badge>)}
            </div>
            <div>
              <div className="text-gray-500 mb-1">{`${orgConfig?.name} Update Date/Time`}</div>
              <div className="text-gray-900 font-medium">
                {harvestRuns?.kt_update_time ? (<>
                        {new Date(harvestRuns.kt_update_time).toLocaleDateString('en-US')}{" "}
                        {new Date(harvestRuns.kt_update_time).toLocaleTimeString('en-US', {hour: "2-digit",minute: "2-digit",})}
                        </>
                      ) : null}
              </div>
            </div>
          </div>
          {/* Row 3 - Client Alert */}
          {chart.clientAlert && (
            <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <div className="text-red-900 font-medium" style={{ fontSize: '11px' }}>
                Client Alert: {chart.clientAlert}
              </div>
            </div>
          )}
        </div>

        {/* Main Data Table */}
        <div className="flex-1 flex flex-col overflow-hidden px-6 py-4">
          {/* Expand/Collapse All Controls */}
          <div className="mb-2 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const allSectionNumbers = new Set(chart.chartData.sections.map(s => s.section_number));
                setExpandedSections(allSectionNumbers);
              }}
              className="text-xs"
            >
              <ChevronDown className="w-3 h-3 mr-1" />
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpandedSections(new Set())}
              className="text-xs"
            >
              <ChevronRight className="w-3 h-3 mr-1" />
              Collapse All
            </Button>
          </div>

          {/* Table Header - Fixed */}
          <div className="bg-white border border-gray-300 rounded-t-lg overflow-hidden">
            {/* Main Column Headers */}
            <div className="grid grid-cols-[70%_30%] border-b border-gray-300">
              <div className="px-4 py-3 border-r border-gray-300 bg-gray-100">
                <h2 className="text-gray-900 uppercase tracking-wide" style={{ fontSize: '11px', fontWeight: '600' }}>
                  Extracted from KanTime
                </h2>
              </div>
              <div className="px-4 py-3 bg-blue-50">
                <h2 className="text-blue-900 uppercase tracking-wide text-center" style={{ fontSize: '11px', fontWeight: '600' }}>
                  AI Generated
                </h2>
              </div>
            </div>

            {/* Sub-headers */}
            <div className="grid grid-cols-[70%_15%_15%] bg-gray-50">
              <div className="px-4 py-2 border-r border-gray-200">
                <p className="text-gray-600" style={{ fontSize: '10px' }}>Section Content</p>
              </div>
              <div className="px-4 py-2 border-r border-gray-200 text-center">
                <p className="text-gray-700" style={{ fontSize: '10px', fontWeight: '500' }}>Adjudication</p>
              </div>
              <div className="px-4 py-2 text-center">
                <p className="text-gray-700" style={{ fontSize: '10px', fontWeight: '500' }}>Note to Clinician</p>
              </div>
            </div>
          </div>

          {/* Table Body - Scrollable */}
          <div className="flex-1 overflow-auto border-l border-r border-b border-gray-300 rounded-b-lg">
            <div className="bg-white">
              {filteredSections.length > 0 ? (
                filteredSections.map(section => renderSection(section))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No sections match your search query
                </div>
              )}

              {/* Footer Sections: Shift Narrative, Notes, Signatures */}
              <div className="mt-8 space-y-6 px-4 pb-8">
                {/* Shift Narrative Section */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h3 className="text-gray-900 font-semibold" style={{ fontSize: '12px' }}>Shift Narrative</h3>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-800" style={{ fontSize: '11px', lineHeight: '1.5' }}>
                      {chart.shiftNarrative || "No shift narrative provided."}
                    </p>
                  </div>
                  <div className="bg-gray-50/50 px-4 py-2 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Decision: <strong>Approved</strong></span>
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Score: <strong>-</strong></span>
                      </div>
                      <span className="text-gray-400" style={{ fontSize: '10px' }}>Note to Clinician: ---</span>
                    </div>
                  </div>
                </div>

                {/* Today's Notes Section */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h3 className="text-gray-900 font-semibold" style={{ fontSize: '12px' }}>Today's Notes</h3>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-800" style={{ fontSize: '11px', lineHeight: '1.5' }}>
                      {chart.todaysNotes || "No notes for today."}
                    </p>
                  </div>
                  <div className="bg-gray-50/50 px-4 py-2 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Decision: <strong>Approved</strong></span>
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Score: <strong>-</strong></span>
                      </div>
                      <span className="text-gray-400" style={{ fontSize: '10px' }}>Suggestion: ---</span>
                    </div>
                  </div>
                </div>

                {/* Signatures Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="grid grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-gray-200 pb-1">
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Clinician Signature Present</span>
                        <span className="text-gray-900 font-medium" style={{ fontSize: '11px' }}>{chart.signaturePresent || "No"}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-200 pb-1">
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Clinician Signature Date</span>
                        <span className="text-gray-900 font-medium" style={{ fontSize: '11px' }}>{chart.clinicianSignature || "N/A"}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-gray-200 pb-1">
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Client Signature Present</span>
                        <span className="text-gray-900 font-medium" style={{ fontSize: '11px' }}>{chart.clientSignedOnDate ? "Yes" : "No"}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-200 pb-1">
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Client Signature Date</span>
                        <span className="text-gray-900 font-medium" style={{ fontSize: '11px' }}>{chart.clientSignedOnDate || "N/A"}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-200 pb-1">
                        <span className="text-gray-500" style={{ fontSize: '10px' }}>Client Signature Time</span>
                        <span className="text-gray-900 font-medium" style={{ fontSize: '11px' }}>{chart.clientSignedOnTime || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
         <AlertDialog open={showAuditActionDialog} onOpenChange={setShowAuditActionDialog}>
              <AlertDialogContent className="max-w-xl sm:max-w-2xl h-[70vh] p-0 flex flex-col">

                {/* Header */}
                <AlertDialogHeader className="p-6 border-b">
                  <AlertDialogTitle>
                    {pendingAuditAction === 'approve' ? 'Agree Chart Review' : 'Disagree Chart Review'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {/* You are about to {pendingAuditAction} this audit for{" "} */}
                    You are about to to  {pendingAuditAction === 'approve' ? 'agree' : 'disagree'} with AI for this eChart
                    <strong>{selectedAuditData?.patientName}</strong>.
                    {pendingAuditAction === 'reject' && ' Please provide a reason for rejection.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>

                {/* Scrollable Body with expanding Textarea */}
                <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
                  <Textarea
                    value={auditActionComment}
                    onChange={(e) => setAuditActionComment(e.target.value)}
                    placeholder={
                      pendingAuditAction === 'approve'
                        ? 'Optional comment...'
                        : 'Reason for rejection (required)...'
                    }
                    className="flex-1 resize-none min-h-[100px] focus:min-h-[200px]"
                  />
                  <div className="p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="font-medium text-gray-800 mb-2">Action will be recorded as:</p>
                    <p className="text-gray-600">
                      {pendingAuditAction === 'approve' ? 'Agreed' : 'Disagreed'} by -{' '}
                      {user?.name + ' ' + user?.lastName}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <AlertDialogFooter className="bg-gradient-to-br from-gray-50 via-blue-50/30 to-white/95 backdrop-blur-sm px-6 py-4 border-t">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmAuditAction}
                    disabled={pendingAuditAction === 'reject' && !auditActionComment.trim()}
                    className={pendingAuditAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {pendingAuditAction === 'approve' ? 'I Agree with AI' : 'I Disagree with AI'}
                  </AlertDialogAction>
                </AlertDialogFooter>

              </AlertDialogContent>
            </AlertDialog>

            <Dialog open={selectedUndoCharts} onOpenChange={()=>setSelectedUndoCharts(null)}>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                 <DialogTitle>Client ID: {harvestRuns?.client_id}</DialogTitle>
                 <DialogDescription>
                    Are you sure you want to undo the decision of this chart?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSelectedUndoCharts(null)}>
                    Close
                  </Button>
                  <Button variant="approve" onClick={handleUndoAuditAction}>
                  {/* <Undo className="w-4 h-4" /> */}
                    Undo
                  </Button>
                </DialogFooter>                      
              </DialogContent>
            </Dialog>
      </div>
    </Layout>
  );
}